import { AfterViewChecked, ChangeDetectorRef, Component, inject, Input, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators, NgModel } from '@angular/forms';
import { DropdownOption, ModalDialogActive } from '@ddv/common-components';
import { FeatureFlagService } from '@ddv/entitlements';
import {
    CellFormat,
    LabelValuePair,
    MetadataFormatUtils,
    AGG_TYPES_BY_SECTION_AND_DISPLAY,
    GridConfigItem,
} from '@ddv/models';
import { isUrlValid } from '@ddv/utils';
import { LegacyWidgetsService, WidgetsService } from '@ddv/widgets';
import { switchMap } from 'rxjs';

import { isDateValid } from '../../../utils/is-date-valid';
import { showInputRange } from '../../../utils/show-input-range';

@Component({
    selector: 'app-grid-column-formatting',
    templateUrl: './column-formatting-grid.component.html',
    styleUrls: ['./column-formatting-grid.component.scss'],
    providers: [NgModel],
    standalone: false,
})
export class ColumnFormattingGridComponent implements OnInit, AfterViewChecked {
    @Input() parentData: GridConfigItem | undefined;
    @Input() clientCode = '';

    protected modalDialogActive = inject(ModalDialogActive<GridConfigItem>);
    private readonly featureFlagService = inject(FeatureFlagService);
    private readonly fb = inject(UntypedFormBuilder);
    private readonly cdr = inject(ChangeDetectorRef);
    private readonly legacyWidgetsService = inject(LegacyWidgetsService);
    private readonly widgetsService = inject(WidgetsService);

    configForm: UntypedFormGroup | undefined;
    displayTypes: DropdownOption[] | undefined;
    dateFormats: DropdownOption[] = [];
    aggTypes: DropdownOption[] | undefined;
    fontStyles: DropdownOption[] | undefined;
    numberFormats: DropdownOption[] | undefined;
    alignmentList: DropdownOption[] | undefined;
    rankOrders: DropdownOption[] = [];
    negativeValues: DropdownOption[] = [];
    currentDisplayType: string | undefined;

    linkBehavior: DropdownOption[] = [
        {
            text: 'Select / Clear selection',
            key: null,
            value: null,
        },
        {
            text: 'Open in Same Window',
            key: '_self',
            value: '_self',
        },
        {
            text: 'Open in New Window',
            key: '_blank',
            value: '_blank',
        },
        {
            text: 'Master/Detail',
            key: 'masterdetail',
            value: 'masterdetail',
        },
    ];
    linkType: DropdownOption[] = [
        {
            text: 'Select / Clear selection',
            key: null,
            value: null,
        },
        {
            text: 'URL',
            key: 'url',
            value: 'url',
        },
        {
            text: 'DDV Widget',
            key: 'ddvwidget',
            value: 'ddvwidget',
        },
    ];
    linkCrossRef: Record<string, string[]> = {
        ddvwidget: ['masterdetail'],
        masterdetail: ['ddvwidget'],
        url: ['_self', '_blank'],
        _blank: ['url'],
        _self: ['url'],
    };
    linkBehaviorFiltered: DropdownOption[] | undefined;
    linkTypeFiltered: DropdownOption[] | undefined;
    ddvWidgets: DropdownOption[] = [];
    linkBehaviorSelected: DropdownOption | undefined;
    linkTypeSelected: DropdownOption | undefined;

    protected valueOperators: DropdownOption[] | undefined;

    ngOnInit(): void {
        if (!this.parentData) {
            return console.error('Cannot initialize this component without parentData');
        }

        this.linkBehaviorFiltered = this.linkBehavior;
        this.linkTypeFiltered = this.linkType;
        this.linkBehaviorSelected = this.linkBehavior[0];
        this.linkTypeSelected = this.linkType[0];

        this.currentDisplayType = this.parentData.displayType;
        this.displayTypes = MetadataFormatUtils.getDisplayTypes(this.parentData.displayType ?? '').map((t) => {
            return { text: t.label, key: t.value, value: t.value };
        });
        this.dateFormats = MetadataFormatUtils.getDateFormats().map((t) => {
            return { text: t, key: t, value: t };
        });
        this.aggTypes = (AGG_TYPES_BY_SECTION_AND_DISPLAY.grid[this.parentData.displayType ?? 'value']).map((t: LabelValuePair<string>) => {
            return { text: t.label, key: t.value, value: t.value };
        });
        this.fontStyles = MetadataFormatUtils.getFonts().map((t) => {
            return { text: t.label, key: t.value, value: t.value };
        });
        this.numberFormats = MetadataFormatUtils.getNumberFormats().map((t) => {
            return { text: t.label, key: t.value, value: t.value };
        });
        this.alignmentList = MetadataFormatUtils.getAlignments().map((t) => {
            return { text: t.label, key: t.value, value: t.value };
        });
        this.negativeValues = MetadataFormatUtils.getNegativeValues().map((v) => {
            return { text: v.label, key: v.value, value: v.value, css: v.value.startsWith('red') ? 'redcolor' : 'blackcolor' };
        });
        this.rankOrders = MetadataFormatUtils.getRankOrders().map((t) => {
            return { text: t.label, key: t.value, value: t.value };
        });
        this.updateValueOperators();
        this.fetchWidgets();
        this.initForm(this.parentData);
    }

    ngAfterViewChecked(): void {
        this.cdr.detectChanges();
    }

    closeGrid(configData: GridConfigItem): void {
        if (!configData.linkBehavior || !configData.linkType) {
            configData.hyperlink = undefined;
        }

        this.modalDialogActive.close(configData);
    }

    onDisplayTypeChanged(e: DropdownOption): void {
        this.configForm?.get('displayType')?.setValue(e.value);
        this.currentDisplayType = this.configForm?.get('displayType')?.value;
        this.updateValueOperators();
    }

    toggleCustomNameField(): void {
        if (this.configForm?.controls.showCustomName.value) {
            this.configForm.controls.customName.enable();
        } else {
            this.configForm?.controls.customName.disable();
        }
    }

    validateUrl(): void {
        const hyperlink = this.configForm?.get('hyperlink')?.value;
        if (hyperlink && !isUrlValid(hyperlink)) {
            this.configForm?.get('hyperlink')?.setErrors({ urlIncorrect: true });
        }
    }

    touchedOrDirty(field?: string): boolean | undefined {
        return !field ? false : !!this.configForm?.get(field)?.touched || !!this.configForm?.get(field)?.dirty;
    }

    updateLinks(selectedOption?: DropdownOption): void {
        if (!selectedOption) {
            return;
        }

        if (selectedOption.value == null) {
            this.linkBehaviorSelected = selectedOption;
            this.linkTypeSelected = selectedOption;
            this.linkBehaviorFiltered = this.linkBehavior;
            this.linkTypeFiltered = this.linkType;
            this.configForm?.get('linkBehavior')?.setValue(null);
            this.configForm?.get('linkType')?.setValue(null);
            return;
        }

        const linkDetails = this.linkCrossRef[selectedOption.value as string];
        switch (selectedOption.value) {
            case 'url':
            case 'ddvwidget':
                this.setUrlOrWidgetLink(selectedOption, linkDetails);
                break;
            case '_self':
            case '_blank':
            case 'masterdetail':
                this.setBehaviorLink(selectedOption, linkDetails);
                break;
        }
    }

    protected onColumnConditionChange(columnCondition: CellFormat[]): void {
        if (!this.configForm) {
            return;
        }
        this.configForm.get('columnCondition')?.setValue(columnCondition);
    }

    private initForm(data: GridConfigItem): void {
        this.configForm = this.fb.group(
            {
                colId: [data.colId],
                value: [data.value],
                customName: [data.customName],
                showCustomName: [data.showCustomName],
                aggregationType: [data.aggregationType],
                displayType: [data.displayType],
                dateFormat: [data.dateFormat ?? 'MM/DD/YYYY'],
                fontStyle: [data.fontStyle],
                numberFormat: [data.numberFormat],
                alignment: [data.alignment],
                decimalPlaces: [data.decimalPlaces],
                negativeValue: [data.negativeValue],
                columnCondition: [[...data.columnCondition ?? []] , [(this.validateColumnCondition.bind(this))]],
                isRanked: [data.isRanked],
                isBlank: [data.isBlank],
                useAbsVal: [data.useAbsVal],
                rankOrder: [data.rankOrder],
                hyperlink: [data.hyperlink],
                linkBehavior: [data.linkBehavior],
                linkType: [data.linkType],
                ddvWidget: [data.ddvWidget],
            },
            {
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                validator: (group: any) => {
                    if (group.get('showCustomName').value) {
                        group.controls.customName.setValidators(Validators.required);
                    } else {
                        group.controls.customName.clearValidators();
                    }

                    if (group.get('linkType').value === 'ddvwidget' && !group.get('ddvWidget').value) {
                        group.get('ddvWidget').setErrors({ ddvWidgetInvalid: true });
                    }
                },
            },
        );

        if (data.linkBehavior) {
            this.linkBehaviorSelected = this.linkBehavior.find((item) => item.value === data.linkBehavior);
        }
        if (data.linkType) {
            this.linkTypeSelected = this.linkType.find((item) => item.value === data.linkType);
        }

        if (data.hyperlink && (!data.linkBehavior || !data.linkType)) {
            if (!data.linkBehavior && !data.linkType) {
                this.linkBehaviorSelected = this.linkBehavior[1];
                this.linkTypeSelected = this.linkType[1];
                this.configForm.get('linkBehavior')?.patchValue(this.linkBehaviorSelected.value);
                this.configForm.get('linkType')?.patchValue(this.linkTypeSelected.value);
            } else if (!data.linkBehavior) {
                this.updateLinks(this.linkTypeSelected);
            } else if (!data.linkType) {
                this.updateLinks(this.linkBehaviorSelected);
            }
        }
        this.toggleCustomNameField();
    }

    private fetchWidgets(): void {
        this.featureFlagService.isFlagEnabled('use-aws-for-widgets', this.clientCode ?? '')
            .pipe(
                switchMap((isFlagEnabled) => {
                    if (isFlagEnabled) {
                        return this.widgetsService.fetchAllWidgetLookups(this.clientCode);
                    }
                    return this.legacyWidgetsService.fetchAllWidgets();
                }),
            )
            .subscribe((data) => {
                this.ddvWidgets = data.map((datum) => ({ key: datum.id, text: datum.name, value: datum.id }));
            });
    }

    private setUrlOrWidgetLink(selectedOption: DropdownOption, linkDetails: string[]): void {
        this.linkTypeSelected = selectedOption;
        this.linkTypeFiltered = this.linkType;
        this.linkBehaviorFiltered = this.linkBehavior.filter((link) => linkDetails.indexOf(link.value) !== -1 || link.value == null);
        if (this.linkBehaviorSelected?.value == null ||
            this.linkBehaviorFiltered.map((item) => item.value).indexOf(this.linkBehaviorSelected.value) === -1
        ) {
            this.linkBehaviorSelected = this.linkBehaviorFiltered[1];
        }
        this.configForm?.get('linkBehavior')?.setValue(this.linkBehaviorSelected.value);
        this.configForm?.get('linkType')?.setValue(this.linkTypeSelected.value);
    }

    private setBehaviorLink(selectedOption: DropdownOption, linkDetails: string[]): void {
        this.linkBehaviorSelected = selectedOption;
        this.linkBehaviorFiltered = this.linkBehavior;
        this.linkTypeFiltered = this.linkType.filter((link) => linkDetails.indexOf(link.value) !== -1 || link.value == null);
        if (this.linkTypeSelected?.value == null ||
            this.linkTypeFiltered.map((item) => item.value).indexOf(this.linkTypeSelected.value) === -1
        ) {
            this.linkTypeSelected = this.linkTypeFiltered[1];
        }
        this.configForm?.get('linkType')?.setValue(this.linkTypeSelected.value);
        this.configForm?.get('linkBehavior')?.setValue(this.linkBehaviorSelected.value);

        const hyperlink = this.configForm?.get('hyperlink')?.value;
        if (hyperlink && !isUrlValid(hyperlink)) {
            this.configForm?.get('hyperlink')?.setValue(null);
        }
    }

    private validateColumnCondition(control: UntypedFormGroup): { [key: string]: boolean } | null {
        for (const condition of control.value as CellFormat[]) {
            if (!condition.operatorCondition) {
                return { operatorConditionMissing: true };
            }

            const isInputRange = showInputRange(condition.operatorCondition, this.valueOperators ?? []);

            if (this.currentDisplayType === 'date' && condition.operatorCondition !== 'ADO') {
                return isDateValid(isInputRange, condition);
            }

            if ((this.currentDisplayType === 'value' || this.currentDisplayType === 'bar') && isInputRange) {
                if (!condition.rangeFrom || !condition.rangeTo) {
                    return { rangeMissing: true };
                }

                if (Number(condition.rangeFrom) > Number(condition.rangeTo)) {
                    return { invalidValue: true };
                }
            }

            if (!isInputRange && condition.operatorCondition !== 'AUTO' && condition.value == null) {
                return { valueMissing: true };
            }
        }

        return null;
    }

    private updateValueOperators(): void {
        this.valueOperators = MetadataFormatUtils.getOperators(this.currentDisplayType ?? '')
            .map((operator) => {
                const valueOperator: DropdownOption = { text: operator.label, key: operator.value, value: operator.value };
                if (operator.showInputRange) {
                    valueOperator.showInputRange = operator.showInputRange;
                }
                return valueOperator;
            });
    }
}
